import React from 'react';
import { PropTypes } from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  Button,
  Typography,
  DialogTitle,
  Dialog,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import Dropzone from 'react-dropzone';
import {
  LoadingView,
} from './LoadingView';

const styles = (theme) => ({
  form: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    paddingTop: theme.spacing(1),
  },
  formItem: {
    marginTop: theme.spacing(2),
  },
  columns: {
    fontSize: 11,
    fontWeight: 300,
  },
  notes: {
    fontSize: 12,
    fontWeight: 300,
    fontStyle: 'italic',
    color: '#666',
  },
  dropzone: {
    marginTop: theme.spacing(2),
    border: '1px solid #aaaaaa',
    borderRadius: 5,
    padding: theme.spacing(2),
    textAlign: 'center',
  },
  validationContainer: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  validationTitle: {
    fontWeight: 600,
    fontSize: 13,
    color: theme.palette.primary.main,
  },
  validationSection: {
    borderRadius: 4,
    padding: theme.spacing(1, 1.5),
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  validationSectionValid: {
    backgroundColor: '#f1f8f1',
    border: '1px solid #c8e6c9',
  },
  validationSectionInvalid: {
    backgroundColor: '#ffebee',
    border: '1px solid #ffcdd2',
  },
  validationSectionMissing: {
    backgroundColor: '#fafafa',
    border: '1px solid #e0e0e0',
  },
  validationSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontWeight: 600,
    fontSize: 12,
  },
  validationFieldList: {
    fontSize: 12,
    lineHeight: 1.6,
    paddingLeft: theme.spacing(3.5),
  },
});

class UploadDialog extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedFile: null,
      selectedType: '',
      selectedColumns: '',
      selectedNotes: '',
      selectedUpdateMode: '',      // 'ADD_ONLY' | 'REPLACE_EXISTING'
      validationResults: null,
      unusedColumns: [],           // expected columns absent from the submitted file
    };
  }

  componentDidUpdate() {
    const { open, types } = this.props;

    // run when dialog opens or when the types list is (re-)loaded
    if (
      open &&                                // dialog visible
      types && types.length === 1 &&         // exactly one choice
      this.state.selectedType === ''         // nothing chosen yet
    ) {
      const uploadType = types[0];
      this.setState({
        selectedType: uploadType.id,
        selectedColumns: uploadType.columns
          ? Object.keys(uploadType.columns).join(', ')
          : uploadType.description,
        selectedNotes: uploadType.notes || '',
        showUpdate: !!uploadType.showUpdateOption,
        selectedUpdateMode: uploadType.showUpdateOption ? 'ADD_ONLY' : '',
      });
    }
  }

  // Simple CSV header line parser that handles quoted values and BOM
  parseCSVLine = (line) => {
    // Strip BOM if present
    const cleaned = line.replace(/^\uFEFF/, '');
    const results = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < cleaned.length; i++) {
      const ch = cleaned[i];
      if (ch === '"') {
        inQuotes = !inQuotes;
      } else if (ch === ',' && !inQuotes) {
        results.push(current.trim());
        current = '';
      } else if (ch === '\r') {
        // ignore carriage returns
      } else {
        current += ch;
      }
    }
    if (current.trim()) results.push(current.trim());
    return results;
  };

  validateFile = () => {
    const { selectedFile, selectedType } = this.state;
    const { types } = this.props;

    if (!selectedFile || !selectedType || !types) return;

    const uploadType = types.find((t) => t.id === selectedType);
    if (!uploadType || !uploadType.columns) {
      this.setState({ validationResults: [], unusedColumns: [] });
      return;
    }

    // Read just the first 8KB — enough for any header row
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      const firstLine = content.split(/\r?\n/)[0];
      const csvHeaders = this.parseCSVLine(firstLine);
      const expectedColumns = uploadType.columns;

      // Check each CSV column against the expected columns for this type
      const results = csvHeaders.map((header) => ({
        header,
        valid: Object.prototype.hasOwnProperty.call(expectedColumns, header),
      }));

      // Columns defined in the mapping that the file didn't include
      const csvHeaderSet = new Set(csvHeaders);
      const unusedColumns = Object.keys(expectedColumns).filter(
        (key) => !csvHeaderSet.has(key),
      );

      this.setState({ validationResults: results, unusedColumns });
    };

    reader.onerror = () => {
      this.setState({ validationResults: [], unusedColumns: [] });
    };

    reader.readAsText(selectedFile.slice(0, 8192));
  };

  render() {
    const {
      classes,
      open,
      uploadState,
      types,
    } = this.props;

    const { validationResults, unusedColumns } = this.state;
    const hasValidation = validationResults !== null;

    return (
      <Dialog
        open={open}
        onClose={this.props.onClose}
        aria-labelledby='form-dialog-title'
        fullWidth
        maxWidth='sm'
        TransitionProps={{
          onExited: this.handleUploadExited,
        }}
      >
        {uploadState !== 'LOADING' && <DialogTitle>Upload</DialogTitle>}
        <DialogContent>
          <form className={classes.form} noValidate autoComplete='off'>
            {
              uploadState === 'EDIT'
              && (
                <>
                  <FormControl key='type'>
                    <InputLabel>CSV Type</InputLabel>
                    {types
                      ? (
                        <Select
                          value={this.state.selectedType}
                          onChange={this.onTypeChange}
                          label='CSV Type'
                        >
                          {types.map((type) => (
                            <MenuItem value={type.id} key={type.id}>{type.name}</MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <LoadingView />
                      )}
                  </FormControl>
                  {this.state.selectedNotes && (
                    <Typography className={classes.notes} style={{ marginTop: 16 }}>
                      {this.state.selectedNotes}
                    </Typography>
                  )}
                  {this.state.selectedColumns && (
                    <FormControl key='columns' className={classes.formItem}>
                      <TextField
                        label='CSV Columns'
                        multiline
                        rows={3}
                        disabled
                        variant='outlined'
                        value={this.state.selectedColumns}
                        InputProps={{
                          inputProps: { className: classes.columns },
                        }}
                      />
                    </FormControl>
                  )}
                  {this.state.selectedType && this.state.showUpdate
                    && (
                      <FormControl key='updateMode' className={classes.formItem}>
                        <InputLabel>Data</InputLabel>
                        <Select
                          value={this.state.selectedUpdateMode}
                          onChange={this.onUpdateModeChange}
                          label='Data'
                        >
                          <MenuItem value='ADD_ONLY'>Add uploaded providers to Label</MenuItem>
                          <MenuItem value='REPLACE_EXISTING'>Replace providers in label with uploaded list</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                </>
              )
            }
            <Dropzone onDrop={this.selectFile} multiple={false}>
              {({ getRootProps, getInputProps }) => (
                <section>
                  {
                    uploadState === 'EDIT'
                    && (
                      <div {...getRootProps()} className={classes.dropzone}>
                        <input {...getInputProps()} />
                        {
                          !this.state.selectedFile
                          && (
                            <Typography style={{ cursor: 'pointer' }}>
                              Drag and drop a file here, or click here to select a file.
                            </Typography>
                          )
                        }
                        {
                          this.state.selectedFile
                          && (
                            <Typography color='primary' style={{ cursor: 'pointer' }}>
                              {this.state.selectedFile.name}
                            </Typography>
                          )
                        }
                      </div>
                    )
                  }
                  {
                    (uploadState === 'LOADING')
                    && <LoadingView label={`${this.props.uploadProgress}% Uploaded`} />
                  }
                  {
                    uploadState === 'SUCCESS'
                    && (
                      <Typography>
                        {!types[0].showUpdateOption ?
                          `Your uploaded was successfully started.
                        Follow the upload status for further details.` :
                          'Your upload has started. Please give the process a few minutes to finish and refresh or navigate back to the page.'}
                      </Typography>
                    )
                  }
                  {
                    uploadState === 'ERROR'
                    && (
                      <Typography>
                        There was a problem uploading your file. Please try again.
                      </Typography>
                    )
                  }
                </section>
              )}
            </Dropzone>

            {/* Validation Results */}
            {uploadState === 'EDIT' && hasValidation && (
              <div className={classes.validationContainer}>
                <Typography className={classes.validationTitle}>
                  Validation Results
                </Typography>

                {/* Valid Fields */}
                {validationResults.filter((r) => r.valid).length > 0 && (
                  <div className={`${classes.validationSection} ${classes.validationSectionValid}`}>
                    <div className={classes.validationSectionHeader}>
                      <CheckCircleIcon style={{ color: '#2e7d32', fontSize: 16 }} />
                      <Typography style={{ fontSize: 12, fontWeight: 600, color: '#2e7d32' }}>
                        {`Valid Fields (${validationResults.filter((r) => r.valid).length})`}
                      </Typography>
                    </div>
                    <Typography className={classes.validationFieldList} style={{ color: '#388e3c' }}>
                      {validationResults.filter((r) => r.valid).map((r) => r.header).join(', ')}
                    </Typography>
                  </div>
                )}

                {/* Invalid Fields */}
                {validationResults.filter((r) => !r.valid).length > 0 && (
                  <div className={`${classes.validationSection} ${classes.validationSectionInvalid}`}>
                    <div className={classes.validationSectionHeader}>
                      <CancelIcon style={{ color: '#c62828', fontSize: 16 }} />
                      <Typography style={{ fontSize: 12, fontWeight: 600, color: '#c62828' }}>
                        {`Invalid Fields (${validationResults.filter((r) => !r.valid).length}) — not recognized for this upload type`}
                      </Typography>
                    </div>
                    <Typography className={classes.validationFieldList} style={{ color: '#c62828' }}>
                      {validationResults.filter((r) => !r.valid).map((r) => r.header).join(', ')}
                    </Typography>
                  </div>
                )}

                {/* Missing Fields */}
                {unusedColumns.length > 0 && (
                  <div className={`${classes.validationSection} ${classes.validationSectionMissing}`}>
                    <div className={classes.validationSectionHeader}>
                      <RemoveCircleOutlineIcon style={{ color: '#757575', fontSize: 16 }} />
                      <Typography style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>
                        {`Missing Fields (${unusedColumns.length}) — supported but not in this file`}
                      </Typography>
                    </div>
                    <Typography className={classes.validationFieldList} style={{ color: '#777' }}>
                      {unusedColumns.join(', ')}
                    </Typography>
                  </div>
                )}
              </div>
            )}
          </form>
        </DialogContent>
        <DialogActions>
          {uploadState === 'EDIT'
            && (
              <>
                <Button onClick={this.props.onClose} color='primary'>
                  Cancel
                </Button>
                <Button
                  variant='outlined'
                  onClick={this.validateFile}
                  color='primary'
                  disabled={!this.state.selectedFile || !this.state.selectedType}
                >
                  Validate
                </Button>
                <Button
                  variant='contained'
                  onClick={this.startUpload}
                  color='primary'
                  disabled={
                    !this.state.selectedFile ||
                    !this.state.selectedType ||
                    (this.state.showUpdate && !this.state.selectedUpdateMode)
                  }
                >
                  Upload
                </Button>
              </>
            )}
          {
            uploadState === 'SUCCESS'
            && (
              <Button onClick={this.props.onClose} color='primary'>
                Close
              </Button>
            )
          }
          {
            uploadState === 'ERROR'
            && (
              <Button onClick={this.props.onClose} color='primary'>
                Close
              </Button>
            )
          }
        </DialogActions>
      </Dialog>
    );
  }

  handleUploadExited = () => {
    this.setState({
      selectedFile: null,
      validationResults: null,
      unusedColumns: [],
    });
    this.props.onExited();
  };

  onUpdateModeChange = (event) => {
    this.setState({ selectedUpdateMode: event.target.value });
  };

  onTypeChange = (event) => {
    const {
      types,
    } = this.props;

    if (types) {
      const uploadType = types.find((type) => type.id === event.target.value);
      if (uploadType) {
        this.setState({
          selectedType: uploadType.id,
          selectedColumns: uploadType.columns ? Object.keys(uploadType.columns).join(', ') : uploadType.description,
          selectedNotes: uploadType.notes || '',
          showUpdate: !!uploadType.showUpdateOption,
          selectedUpdateMode: uploadType.showUpdateOption ? 'ADD_ONLY' : '',
          validationResults: null,
      unusedColumns: [],
        });
      } else {
        this.setState({
          selectedType: '',
          selectedColumns: '',
          selectedNotes: '',
          showUpdate: false,
          selectedUpdateMode: '',
          validationResults: null,
      unusedColumns: [],
        });
      }
    }
  };

  selectFile = (files) => {
    this.setState({
      selectedFile: files[0],
      validationResults: null,
      unusedColumns: [],
    });
  };

  startUpload = () => {
    if (this.state.selectedType && this.state.selectedFile) {
      this.props.onUpload(
        this.state.selectedType,
        this.state.selectedFile,
        this.state.selectedUpdateMode
      );
    }
  };
}

UploadDialog.propTypes = {
  classes: PropTypes.object.isRequired,
  types: PropTypes.array,
  uploadState: PropTypes.string.isRequired,
  uploadProgress: PropTypes.number,
  open: PropTypes.bool.isRequired,
  onUpload: PropTypes.func.isRequired,
  onExited: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

const styled = withStyles(styles)(UploadDialog);
export { styled as UploadDialog };
